import {
  Flex,
  IconButton,
  Icon,
  Text,
  Button,
  Dropdown,
  EmblemAnimated,
  LogoAnimated,
  icon as iconSize,
  iconWeight,
} from '@nula/core-ui';
import { CanvasEmptyIllustration, TrashIllustration } from '@/components/empty-illustrations';
import { useCanvasPreviewRenderer } from '@/lib/canvas/canvas-preview-context';
import type { CanvasEmbedState } from './canvas-embed-state';
import styles from './canvas-embed-card.module.css';

export interface CanvasEmbedCardProps {
  /** The canvas entity to embed. */
  canvasId: string;
  /** The canvas title, shown top-left in the live state. */
  title?: string;
  /** Render state derived from the canvas's workspace row. Defaults to `live`. */
  state?: CanvasEmbedState;
  /** Open the canvas in a new tab. */
  onOpen: () => void;
  /** Remove the embed from the document. */
  onRemove: () => void;
  /** Restore the archived canvas, then open it. Only used in the archived state. */
  onRestore?: () => void;
}

function OptionsMenu({ onRemove }: { onRemove: () => void }) {
  return (
    <Dropdown
      menu={{
        items: [
          {
            key: 'remove',
            label: 'Remove',
            danger: true,
            icon: <Icon name="delete" size={iconSize.md} weight={iconWeight.light} />,
            onClick: onRemove,
          },
        ],
      }}
      trigger={['click']}
      placement="bottom-end"
    >
      <IconButton size="sm" transparent pill aria-label="Canvas options" className={styles.control}>
        <Icon name="more_horiz" size={iconSize.sm} weight={iconWeight.light} />
      </IconButton>
    </Dropdown>
  );
}

/**
 * Embedded-canvas card. When live it mounts the registered read-only canvas
 * renderer ({@link useCanvasPreviewRenderer}) so every node type renders with
 * full fidelity and the Yjs sync/loading is handled for us. Floating Open / ⋯
 * controls overlay the top-right corner, revealed on hover.
 *
 * When the canvas is archived or deleted it renders a calm monochrome fallback
 * using the shared empty-state illustrations, with the appropriate recovery
 * action (Restore for archived, Remove for deleted). The ⋯ Remove menu is
 * always available so a dead embed can be cleared.
 */
export function CanvasEmbedCard({
  canvasId,
  title,
  state = 'live',
  onOpen,
  onRemove,
  onRestore,
}: CanvasEmbedCardProps) {
  const CanvasRenderer = useCanvasPreviewRenderer();

  if (state === 'pending') {
    return (
      <div className={styles.card} contentEditable={false}>
        <div className={styles.body}>
          <Flex
            align="center"
            justify="center"
            className={styles.skeleton}
            data-testid="canvas-embed-pending"
          >
            <LogoAnimated size={300} loop duration={2400} holdDuration={600} className={styles.skeletonLogo} />
          </Flex>
        </div>
      </div>
    );
  }

  if (state === 'archived') {
    return (
      <div className={styles.card} contentEditable={false}>
        <div className={styles.controls}>
          <OptionsMenu onRemove={onRemove} />
        </div>
        <Flex vertical align="center" justify="center" gap="sm" className={styles.fallback}>
          <CanvasEmptyIllustration className={styles.illustration} />
          <Text className={styles.fallbackTitle}>Canvas archived</Text>
          <Text type="secondary" size="sm" className={styles.fallbackSub}>
            This canvas is in the trash. Restore it to view the embed.
          </Text>
          {onRestore && (
            <Button size="sm" onClick={onRestore}>
              Restore &amp; open
            </Button>
          )}
        </Flex>
      </div>
    );
  }

  if (state === 'deleted') {
    return (
      <div className={styles.card} contentEditable={false}>
        <div className={styles.controls}>
          <OptionsMenu onRemove={onRemove} />
        </div>
        <Flex vertical align="center" justify="center" gap="sm" className={styles.fallback}>
          <TrashIllustration className={styles.illustration} />
          <Text className={styles.fallbackTitle}>Canvas unavailable</Text>
          <Text type="secondary" size="sm" className={styles.fallbackSub}>
            This canvas was deleted and can no longer be displayed.
          </Text>
          <Button size="sm" onClick={onRemove}>
            Remove embed
          </Button>
        </Flex>
      </div>
    );
  }

  return (
    <div className={styles.card} contentEditable={false}>
      {title && (
        <div className={styles.titleTag}>
          <Text size="sm" className={styles.titleText}>
            {title}
          </Text>
        </div>
      )}
      <div className={styles.controls}>
        <IconButton
          size="sm"
          transparent
          pill
          aria-label="Open canvas"
          className={styles.control}
          onClick={onOpen}
        >
          <Icon name="open_in_new" size={iconSize.sm} weight={iconWeight.light} />
        </IconButton>
        <OptionsMenu onRemove={onRemove} />
      </div>

      <div className={styles.body}>
        {CanvasRenderer ? (
          <CanvasRenderer entityId={canvasId} />
        ) : (
          <Flex
            align="center"
            justify="center"
            className={styles.loading}
            data-testid="canvas-embed-loading"
          >
            <EmblemAnimated size={48} color="var(--outline-variant)" />
          </Flex>
        )}
      </div>
    </div>
  );
}
